import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import style from "./Tabs.module.css";
import { Icon } from "@/components/general/icon/Icon";
import { EIcon } from "@/utils/enums";
import { Button } from "@pepperstone/pepper-ui-kit";

export function TabsList({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const scrollAmount = 150;

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflow({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useLayoutEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(el);

    el.addEventListener("scroll", updateOverflow);
    return () => {
      el.removeEventListener("scroll", updateOverflow);
      resizeObserver.disconnect();
    };
  }, [updateOverflow]);

  return (
    <section className={style.tabs__wrapper}>
      {overflow.left && (
        <Button
          className={`${style.tabs__arrow} ${style["tabs__arrow--left"]}`}
          type="button"
          variant="ps-btn-outline"
          onClick={() => scroll("left")}
        >
          <Icon name={EIcon.ArrowUp} />
        </Button>
      )}
      <div className={style.tabs__nav} ref={scrollRef}>
        {children}
      </div>
      {overflow.right && (
        <Button
          className={`${style.tabs__arrow} ${style["tabs__arrow--right"]}`}
          type="button"
          variant="ps-btn-outline"
          onClick={() => scroll("right")}
        >
          <Icon name={EIcon.ArrowUp} />
        </Button>
      )}
    </section>
  );
}
